import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { formatDate } from "../../utils/date";
import { protectGuestPortal } from "../../utils/access";
import { sendRequest } from "../../utils/api";
import Booking from "../../models/Booking";
import Order from "../../models/Order";
import BookedRoom from "../../models/BookedRoom";
import { padNumber } from "../../utils/string";
import FullPageSpinner from "../../components/FullPageSpinner";
import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ConfirmModalContext";
import { clearBill } from "../../utils/bill";
import Customer from "../../models/Customer";
import { DEFAULT_CUSTOMER_EMAIL } from "../../constants";


const GuestBillPage: React.FC = () => {
    const { openModal, closeModal } = useModal();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [searchParams] = useSearchParams();

    const [bookings, setBookings] = useState<(BookedRoom & { roomName: string })[]>([]);
    const [orders, setOrders] = useState<Order[]>([]);
    const [billId, setBillId] = useState("");

    const [loading, setLoading] = useState(false);

    const totals = bookings.reduce(
        (acc, b) => {
            acc.totalBooking += Number(b.amount || 0);
            acc.totalDiscount += Number(b.discount || 0);
            return acc;
        },
        { totalBooking: 0, totalDiscount: 0 }
    );
    const totalOrders = orders.reduce(
        (sum, o) => sum + (o?.orderItems ?? []).reduce((s, i) => s + i.amount, 0), 0
    );

    const grandTotal = totals.totalBooking + totalOrders;

    const totalUnpaidOrders = orders.filter(o => !o.isPaid)
        .reduce((sum, o) => sum + (o.orderItems ?? [])
            .reduce((s, i) => s + i.amount, 0), 0);
    const totalUnpaidBookings = bookings.filter(b => !b.isPaid)
        .reduce((sum, b) => sum + b.amount, 0) - totals.totalDiscount;

    const unPaid = (totalUnpaidBookings < 0 ? 0 : totalUnpaidBookings) + totalUnpaidOrders;


    const totalPaidBookings = bookings
        .filter(b => b.isPaid)
        .reduce((sum, b) => sum + (Number(b.amount || 0)), 0) - totals.totalDiscount

    const totalPaidOrders = orders
        .filter(o => o.isPaid)
        .reduce(
            (sum, o) =>
                sum +
                (o.orderItems ?? []).reduce(
                    (s, i) => s + (Number(i.amount || 0)),
                    0
                ),
            0
        )

    // console.log({ totalPaidBookings, totalPaidOrders })
    const totalPaid = (totalPaidBookings < 0 ? 0 : totalPaidBookings) + totalPaidOrders;


    useEffect(() => {
        protectGuestPortal(navigate);

        // console.log({ ref, billId });

        const billStr = localStorage.getItem("bill");
        const guestBill = async () => {
            setLoading(true);
            if (billStr) {
                try {
                    const bill = JSON.parse(billStr);
                    const { bookings, orders } = await sendRequest("GET", `/guests/bills/${bill.id}`);
                    setBookings(bookings ?? []);
                    setOrders(orders ?? []);
                    setBillId(bill.id);
                } catch (err: any) {
                    if (err.status === 401) showToast("Session Timeout.", { type: "error" });
                    else showToast("An error occured. Please try again.", { type: "error" });
                } finally {
                    setLoading(false);
                }
            }
        }
        guestBill();
    }, []);


    const handleClearingBill = async () => {
        try {

            if (unPaid === 0) {
                return openModal(
                    "All Settled ✅",
                    "No unpaid bills. Everything is up to date!",
                    () => { }, false, "Close"
                );
            }

            setLoading(true);


            const guestStr = localStorage.getItem("user");

            let guest: Customer | null = null;
            if (guestStr) guest = JSON.parse(guestStr);

            const callback_url = (
                `${window.location.origin}/payment/verify?billId=${billId}&type=services`
            );

            const url = "/payment/initiate";
            const response = await sendRequest("POST", url, {
                amount: unPaid, callback_url,
                email: guest?.email ?? DEFAULT_CUSTOMER_EMAIL,
            });

            window.location.href = response.data.data.checkout_url;

            // return true;
        } catch (error: any) {

            console.log(error)
            if (error.status === 401) showToast("Session Timeout.", { type: "error" });
            else
                showToast(
                    "Failed to Clear Bill. Please try again.", { type: "error" }
                );

        } finally {
            setLoading(false);
        }
    };

    //   const handlePrint = () => {
    //     window.print();
    //   };

    return (
        <div className="min-h-screen bg-gray-50 p-4">
            <FullPageSpinner show={loading} />
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate("/guests/dashboard")} className="text-gray-600 hover:text-gray-800 transition">
                        {FaArrowLeft({ size: 22 })}
                    </button>
                    <h1 className="text-3xl font-bold text-gray-800">My Bill</h1>
                </div>
                {/* <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
        >
          {FaPrint({})} Print
        </button> */}
            </div>

            {/* Main Content */}
            <div className="grid md:grid-cols-2 gap-6">
                {/* Room Bookings */}
                <div>
                    <h3 className="text-lg font-semibold mb-4">Room Charges</h3>
                    {bookings.map(b => (
                        <div
                            key={b.id}
                            className="bg-white shadow rounded-xl p-5 mb-4 hover:shadow-2xl transition cursor-pointer hover:-translate-y-1"
                        // onClick={() => navigate(`/guest/bill/room/${b.id}`)}
                        >
                            <div className="flex justify-between items-center mb-2">
                                <h4 className="font-semibold text-gray-800"><span className="capitalize">{b.roomName}</span> / {b.roomNumber}</h4>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${b.isPaid ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                    {b.isPaid ? "Paid" : "Unpaid"}
                                </span>
                            </div>
                            <div className="text-gray-600 text-sm flex justify-between">
                                <span>Checkin: {formatDate(b.checkInDate)}</span>
                                <span>Checkout: {formatDate(b.checkOutDate)}</span>
                                <span className="font-semibold text-indigo-600">₦{b?.amount?.toLocaleString()}</span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Orders */}
                {orders.length > 0 && (<div>
                    <h3 className="text-lg font-semibold mb-4">Orders</h3>
                    {orders.map(o => (
                        <div
                            key={o.orderNo}
                            className="bg-white shadow rounded-xl mb-4 hover:shadow-2xl transition cursor-pointer hover:-translate-y-1"
                        // onClick={() => navigate(`/guest/bill/order/${o.orderNo}`)}
                        >
                            <div className="flex justify-between p-3 border-b bg-gray-50">
                                <span className="font-medium">Order #{padNumber(o.orderNo ?? 0)}</span>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${o.isPaid ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                    {o.isPaid ? "Paid" : "Unpaid"}
                                </span>
                            </div>
                            <div className="p-3 text-sm space-y-1">
                                {(o.orderItems ?? []).map((item, idx) => (
                                    <div key={idx} className="flex justify-between">
                                        <span>{item.itemName} x{item.quantity}</span>
                                        <span>₦{item.amount.toLocaleString()}</span>
                                    </div>
                                ))}
                                <div className="flex justify-between font-semibold border-t pt-2 mt-2">
                                    <span>Total</span>
                                    <span>₦{(o.orderItems ?? []).reduce((sum, i) => sum + i.amount, 0).toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>)}
            </div>

            {/* Grand Total */}
            <div className="bg-white rounded-xl shadow p-6 mt-6 flex flex-col md:flex-row justify-between items-center gap-4 sticky top-[calc(100vh-100px)] z-10">
                <div className="flex flex-col text-gray-700">
                    <span className="text-gray-900 font-semibold">
                        Grand Total: ₦{grandTotal.toLocaleString()}
                    </span>

                    {totals.totalDiscount > 0 && <span className="text-yellow-600">
                        Discount: ₦{totals.totalDiscount.toLocaleString()}
                    </span>}

                    {totalPaid > 0 && (
                        <span className="text-green-700">Paid: ₦{(totalPaid).toLocaleString()}</span>)}

                    {unPaid > 0
                        && (<span className="text-red-700">
                            Unpaid: ₦{(unPaid > 0 ? unPaid : 0).toLocaleString()}
                        </span>)}
                </div>
                <button
                    onClick={handleClearingBill}
                    className="px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
                >
                    Pay Bill
                </button>
            </div>
        </div>
    );
};

export default GuestBillPage;